import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { api } from '../utils/api';

const EditDBADialog = ({ dba, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    dba_name: '',
    county: '',
    registration_date: '',
    expiration_date: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dba) {
      setFormData({
        dba_name: dba.dba_name || '',
        county: dba.county || '',
        registration_date: dba.registration_date ? new Date(dba.registration_date).toISOString().split('T')[0] : '',
        expiration_date: dba.expiration_date ? new Date(dba.expiration_date).toISOString().split('T')[0] : '',
        status: dba.status || 'active'
      });
    }
  }, [dba]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updates = {
        ...formData
      };
      
      if (formData.registration_date) {
        updates.registration_date = new Date(formData.registration_date).toISOString();
      }
      if (formData.expiration_date) {
        updates.expiration_date = new Date(formData.expiration_date).toISOString();
      }

      await api.updateDBA(dba.dba_id, updates);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to update DBA:', error);
      alert('Failed to update DBA');
    } finally {
      setLoading(false);
    }
  };

  if (!dba) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="edit-dba-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit DBA</DialogTitle>
          <DialogDescription>
            Update DBA information for {dba.dba_name} - {dba.state}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="dba_name">DBA Name</Label>
              <Input
                id="dba_name"
                value={formData.dba_name}
                onChange={(e) => setFormData({ ...formData, dba_name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="county">County</Label>
              <Input
                id="county"
                value={formData.county}
                onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                placeholder="County name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="registration_date">Registration Date</Label>
              <Input
                id="registration_date"
                type="date"
                value={formData.registration_date}
                onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiration_date">Expiration Date</Label>
              <Input
                id="expiration_date"
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditDBADialog;